import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    DistributionType,
    EstateFlowchartState,
    EstateToggle,
    FlowchartEstateItemType
} from "../../../EstateFlowchart/model/EstateFlowchartTypes";
import {estateFlowchartApiClient, ResponseStatus} from "../../../EstateFlowchart/client/EstateFlowchartApiClient";
import {MemberGroup} from "../../../ClientManagement/models/InvestorGroupType";
import {clientManagementApiClient} from "../../../ClientManagement/ClientManagementApiClient";
import {referenceDataClient} from "../../../core/ReferenceDataClient";
import {
    resetEstateFlowchartState,
    setEstateFlowchartState,
    setFamilyMembers,
    setListOfStates,
    setSelectedFlowchartId
} from "../../../EstateFlowchart/slices/EstateFlowChartSlice";
import {ReferenceDataType} from "../../../models/referenceData/ReferenceDataType";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import PrintViewWrapper from "../PrintViewWrapper";

import EstateFlowchart from "../../../EstateFlowchart/components/EstateFlowchart/EstateFlowchart";
import DocumentsReviewed from "./DocumentsReviewed";
import {ProfileResponse} from "../../../ClientManagement/models/ProfileResponse";
import {selectReleaseToggles} from '../../../ReleaseToggles/releaseTogglesSlice';
import {
    filterWildcardTrustsByEstateToggleType,
    findMiscellaneousById,
    findTrustInFlowchart,
    findWillById,
    hasWillSummary
} from "../../../EstateFlowchart/utils/DefUtils";
import {
    CardDetailFieldsSections,
    createFilteredDrawerFieldConfig,
    getCardDetails
} from "../../../EstateFlowchart/utils/DrawerUtils";
import EstateCardDetailsSplitView from "./EstateCardDetailsSplitView";
import {IrrevocableTrust} from "../../../EstateFlowchart/model/IrrevocableTrust";

type EstateFlowchartReportWrapperProps = {
    selectedFlowchartId: string,
    displayName: string,
    profile: ProfileResponse
}

interface Page {
    items: IrrevocableTrust[],
    height: number
}

// Suppress "ResizeObserver loop limit exceeded" warning
const originalConsoleError = console.error;

console.error = (...args) => {

    if ((args[0] && (typeof args[0] === 'string' || Array.isArray(args[0]))) && (args[0]?.includes("ResizeObserver loop limit exceeded")
        || args[0]?.includes("ResizeObserver loop completed with undelivered notifications"))) {
        return; // Suppress the warning
    }
    originalConsoleError(...args); // Log other errors as usual
};

const TOTAL_PAGE_HEIGHT = 775;
const HEIGHT_WITHOUT_ROA = 52;

const EstateFlowchartReportWrapper: React.FC<EstateFlowchartReportWrapperProps> = ({
                                                                                       selectedFlowchartId,
                                                                                       displayName,
                                                                                       profile,
                                                                                   }) => {
    const id = profile.id;
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [estateFlowchartStateData, setEstateFlowchartStateData] = useState<EstateFlowchartState>();
    const releaseToggles = useAppSelector(selectReleaseToggles);

    const [trustToCardIdsMap, setTrustToCardIdsMap] = useState<Record<string, string[]>>({});
    const fullEstateRendered = useRef<HTMLDivElement>(null);

    const getFlowcharts = async (): Promise<ResponseStatus> => {
        return await estateFlowchartApiClient.getFlowcharts(id);
    }

    const getFamilyMembers = async (): Promise<MemberGroup> => {
        return clientManagementApiClient.getMemberGroup(id);
    }

    const getListOfStates = async (): Promise<any> => {
        return referenceDataClient.getReferenceData()
    }

    useEffect(() => {
        Promise.all([getFlowcharts(), getFamilyMembers()])
            .then(([flowchartsRes, memberGroup]) => {
                const flowcharts = flowchartsRes.data as EstateFlowchartState[];
                if (flowcharts && flowcharts.length > 0) {
                    const flowChart: EstateFlowchartState = flowcharts.find(ele => ele.flowchartId?.trim() === selectedFlowchartId.trim())!;
                    setEstateFlowchartStateData(flowChart);

                    dispatch(setEstateFlowchartState(flowcharts));
                    dispatch(setSelectedFlowchartId(selectedFlowchartId));
                } else {
                    dispatch(resetEstateFlowchartState());
                }
                dispatch(setFamilyMembers(memberGroup));
            })
            .finally(() => {
                setLoading(false);
            });

        getListOfStates().then((data: ReferenceDataType) => {
            dispatch(setListOfStates(data.statesOfResidency));
        })
    }, [id]);

    useEffect(() => {
        const processCards = (estateCards: HTMLDivElement[], flowchartHasOnlyWills: boolean): Record<string, string[]> => {
            const trustToCardIdsMap: Record<string, string[]> = {};

            estateCards.forEach(card => {
                const parentId = card.dataset.parentId;
                const cardId = card.id.replace("estate-card-", "");

                if (parentId) {
                    if (!trustToCardIdsMap[parentId]) {
                        trustToCardIdsMap[parentId] = initializeTrustToCardIdsMap(estateCards, card, parentId, flowchartHasOnlyWills);
                    }
                    if (parentId !== cardId) {
                        trustToCardIdsMap[parentId].push(cardId);
                    }
                }
            });

            return trustToCardIdsMap;
        };

        const initializeTrustToCardIdsMap = (estateCards: HTMLDivElement[], card: HTMLDivElement, parentId: string, flowchartHasOnlyWills: boolean): string[] => {
            if (card.dataset.parentEstateItemType === FlowchartEstateItemType.REVOCABLE) {
                const wills = estateCards.filter(c => c.dataset.parentEstateItemType === FlowchartEstateItemType.WILL);
                if (wills.length > 0) {
                    const ids = [wills[0].id.replace("estate-card-", ""), parentId];
                    if (wills.length === 2) {
                        ids.push(wills[1].id.replace("estate-card-", ""));
                    }
                    return ids;
                }
            } else if (card.dataset.parentEstateItemType === FlowchartEstateItemType.WILL && !flowchartHasOnlyWills) {
                return [];
            }
            return [parentId];
        };

        setTimeout(() => {
            if (!loading && fullEstateRendered.current) {
                const flowchartHasOnlyWills = hasWillSummary(estateFlowchartStateData?.willSummary)
                    && !estateFlowchartStateData?.revocableTrust;

                const estateCards = Array.from(fullEstateRendered.current.getElementsByClassName("estate-card")) as HTMLDivElement[];
                const trustToCardIdsMap = processCards(estateCards, flowchartHasOnlyWills);
                setTrustToCardIdsMap(trustToCardIdsMap);
            }
        }, 10);
    }, [estateFlowchartStateData, fullEstateRendered.current, loading]);

    const [estateFlowchartStateDataList, setEstateFlowchartStateDataList] = useState<EstateFlowchartState[]>([]);

    const heightWithRoa = (roaText: string) => {
        const charsPerLine = 135;
        const lineHeight = 25;
        const headingHeight = 76;
        const lines = Math.min(Math.ceil(roaText.length / charsPerLine), 4);
        return (lineHeight * lines) + headingHeight;
    }

    useEffect(() => {
        const irrevocables = estateFlowchartStateData?.irrevocableTrusts;
        if (irrevocables && irrevocables?.length > 0) {
            let firstIrrev = irrevocables[0];

            let initialHeight = 0;
            initialHeight += estateFlowchartStateData?.revocableTrust?.recordOfAmendment ? heightWithRoa(estateFlowchartStateData?.revocableTrust?.recordOfAmendment) : HEIGHT_WITHOUT_ROA;
            initialHeight += estateFlowchartStateData?.willSummary?.primaryWill?.recordOfAmendment ? heightWithRoa(estateFlowchartStateData?.willSummary?.primaryWill?.recordOfAmendment) : HEIGHT_WITHOUT_ROA;
            initialHeight += estateFlowchartStateData?.willSummary?.partnerWill?.recordOfAmendment ? heightWithRoa(estateFlowchartStateData?.willSummary?.partnerWill?.recordOfAmendment) : HEIGHT_WITHOUT_ROA;

            const pages: any[] = irrevocables?.reduce((acc: Page[], item) => {
                let currentPage: any = acc[acc.length - 1];
                let height = item.recordOfAmendment && item.recordOfAmendment.length > 0 ? heightWithRoa(item.recordOfAmendment) : HEIGHT_WITHOUT_ROA;
                if (!currentPage || currentPage.height + height > TOTAL_PAGE_HEIGHT) {
                    currentPage = {
                        items: [],
                        height: 0
                    };
                    acc.push(currentPage);
                }
                currentPage.items.push(item);
                if (item.trustId === firstIrrev?.trustId) {
                    currentPage.height = initialHeight;
                }
                currentPage.height += height;
                return acc;
            }, []);
            const result = pages?.map((page: any) => page.items);
            const firstGroup = result?.slice(0, 1);
            const restGroups = result?.slice(1);

            if (firstGroup) {
                setEstateFlowchartStateDataList([{
                    ...estateFlowchartStateData,
                    willSummary: estateFlowchartStateData?.willSummary,
                    revocableTrust: estateFlowchartStateData?.revocableTrust,
                    irrevocableTrusts: firstGroup[0]
                }, ...restGroups.map(group => ({
                    ...estateFlowchartStateData,
                    willSummary: undefined,
                    revocableTrust: undefined,
                    irrevocableTrusts: group
                }))
                ]);
            }
        } else {
            setEstateFlowchartStateDataList([{
                ...estateFlowchartStateData!,
                willSummary: estateFlowchartStateData?.willSummary,
                revocableTrust: estateFlowchartStateData?.revocableTrust,
                irrevocableTrusts: []
            }]);
        }
    }, [estateFlowchartStateData]);

    const getAvailableEstateTypes = (flowChart: EstateFlowchartState) => {
        const inEstateMiscTrusts = (releaseToggles?.enableWildCardInEstateFlowchart && flowChart.wildcardTrusts && flowChart.wildcardTrusts.length > 0)
            ? filterWildcardTrustsByEstateToggleType(flowChart.wildcardTrusts, EstateToggle.IN_ESTATE)
            : [];
        const hasInEstate = estateFlowchartStateData?.willSummary?.primaryWill ||
            estateFlowchartStateData?.willSummary?.partnerWill ||
            estateFlowchartStateData?.revocableTrust ||
            inEstateMiscTrusts.length > 0;

        const outOfEstateMiscTrusts = (releaseToggles?.enableWildCardInEstateFlowchart && flowChart.wildcardTrusts && flowChart.wildcardTrusts.length > 0)
            ? filterWildcardTrustsByEstateToggleType(flowChart.wildcardTrusts, EstateToggle.OUT_OF_ESTATE)
            : [];
        const hasOutOfEstate = (flowChart.irrevocableTrusts && flowChart.irrevocableTrusts.length > 0) || outOfEstateMiscTrusts.length > 0;
        const onlyHasInEstate = hasInEstate && !hasOutOfEstate;
        const onlyHasOutOfEstate = !hasInEstate && hasOutOfEstate;

        return {hasInEstate, hasOutOfEstate, showOnlyFullEstate: onlyHasInEstate || onlyHasOutOfEstate};
    }

    const getSelectedCardDetailsAndFields = (
        estateFlowchartStateData: EstateFlowchartState,
        parentEstateItemType: FlowchartEstateItemType,
        estateItemParentNickName: string,
        estateItemParentTrustId: string,
        estateItemId: string,
    ) => {
        const will = findWillById(estateItemId, estateFlowchartStateData);
        const flowchartTrust = findTrustInFlowchart(
            estateItemId,
            estateItemParentTrustId,
            parentEstateItemType,
            estateFlowchartStateData
        );
        const miscTrust = findMiscellaneousById(estateItemId, estateFlowchartStateData);
        const trust = will ?? flowchartTrust ?? miscTrust;

        let cardType: FlowchartEstateItemType | DistributionType = parentEstateItemType;
        let cardTitle: string = estateItemParentNickName;
        const isWill = trust && 'primaryWill' in trust;
        const isMiscTrust = trust && parentEstateItemType === FlowchartEstateItemType.MISCELLANEOUS;

        if (trust) {
            parentEstateItemType = isWill ? FlowchartEstateItemType.WILL : parentEstateItemType;
            if (isWill) {
                cardType = FlowchartEstateItemType.WILL;
                cardTitle = trust.nickName;
            } else if (isMiscTrust) {
                cardType = FlowchartEstateItemType.MISCELLANEOUS;
                cardTitle = trust.name!;
            } else {
                cardType = (trust as any).distributionType;
                cardTitle = (trust as any).name!;
            }
        }

        const selectedCardInfo = {
            parentEstateItemId: !isWill ? estateItemParentTrustId : estateItemId,
            parentEstateItemType,
            id: estateItemId,
            cardType,
            title: cardTitle
        };

        const cardDetails = getCardDetails(selectedCardInfo, estateFlowchartStateData);
        if (!cardDetails) {
            return null;
        }

        const cardDetailByFields = createFilteredDrawerFieldConfig(
            cardDetails,
            selectedCardInfo.cardType,
            "powersOfAppointment" in cardDetails ? cardDetails.powersOfAppointment : []
        )

        return {selectedCardInfo, cardDetails, cardDetailByFields};
    }

    const renderIrrevocableTrusts = (
        estateFlowchartStateData: EstateFlowchartState,
        trustToCardIdsMap: Record<string, string[]>,
        selectedFlowchartId: string,
        displayName: string
    ) => {
        return estateFlowchartStateData?.irrevocableTrusts?.map(irrevTrust => {
            const filteredTrusts = Object.entries(trustToCardIdsMap).filter(([trustId]) => trustId === irrevTrust.trustId);

            return filteredTrusts.map(([trustId, cardIds]) => {
                return renderCardDetails(
                    estateFlowchartStateData,
                    cardIds,
                    FlowchartEstateItemType.IRREVOCABLE,
                    irrevTrust?.nickName || "",
                    trustId,
                    selectedFlowchartId,
                    displayName
                );
            });
        });
    };

    const renderMiscTrusts = (estateFlowchartStateData: EstateFlowchartState, estateToggleType: EstateToggle) => {
        if (!releaseToggles?.enableWildCardInEstateFlowchart || !estateFlowchartStateData.wildcardTrusts) return null;
        const miscTrusts = filterWildcardTrustsByEstateToggleType(estateFlowchartStateData.wildcardTrusts, estateToggleType);
        if (!miscTrusts.length) return null;
        return miscTrusts.map(trust => {
            return renderCardDetails(
                estateFlowchartStateData,
                [trust.trustId],
                FlowchartEstateItemType.MISCELLANEOUS,
                trust.name ?? '',
                trust.trustId,
                selectedFlowchartId,
                displayName
            );
        });
    };

    function mapToSplitView(result: {
        selectedCardInfo: {
            parentEstateItemId: string;
            parentEstateItemType: FlowchartEstateItemType;
            id: string;
            cardType: FlowchartEstateItemType | DistributionType;
            title: string
        };
        cardDetails: any;
        cardDetailByFields: CardDetailFieldsSections[]
    }, estateItemType: FlowchartEstateItemType, estateFlowchartStateData: EstateFlowchartState, trustId: string, cardId: string, selectedFlowchartId: string, displayName: string) {
        return result.cardDetailByFields.map((cardDetailFieldsForPage, index) => {
            const selectedCardDetails = index > 0
                ? {...result.selectedCardInfo, title: `${result.selectedCardInfo.title} (Continued)`}
                : result.selectedCardInfo;

            const isOutOfEstateMiscTrust = estateItemType === FlowchartEstateItemType.MISCELLANEOUS
                && filterWildcardTrustsByEstateToggleType(estateFlowchartStateData.wildcardTrusts || [], EstateToggle.OUT_OF_ESTATE)
                    .some(trust => trust.trustId === trustId);

            return (
                <EstateCardDetailsSplitView
                    key={`estate-card-details-split-view-${cardId}`}
                    estateName={(estateItemType === FlowchartEstateItemType.IRREVOCABLE || isOutOfEstateMiscTrust) ? "Out of Estate" : "In Estate"}
                    flowchartId={selectedFlowchartId}
                    selectedCardId={cardId}
                    selectedCardDetails={selectedCardDetails}
                    displayName={displayName}
                    sideDrawerItem={result.cardDetails}
                    sideDrawerFields={cardDetailFieldsForPage}
                />
            );
        });
    }

    const renderCardDetails = (
        estateFlowchartStateData: EstateFlowchartState,
        cardIds: string[],
        estateItemType: FlowchartEstateItemType,
        estateItemNickName: string,
        trustId: string,
        selectedFlowchartId: string,
        displayName: string
    ) => {
        return cardIds.map(cardId => {
            const result = getSelectedCardDetailsAndFields(
                estateFlowchartStateData,
                estateItemType,
                estateItemNickName,
                trustId,
                cardId
            );

            if (!result) {
                return null;
            }

            return mapToSplitView(result, estateItemType, estateFlowchartStateData, trustId, cardId, selectedFlowchartId, displayName);
        });
    };

    const renderEstateItemsAndCardDetailsSplitView = useMemo(() => {
        if (!fullEstateRendered.current || !estateFlowchartStateData || Object.keys(trustToCardIdsMap).length === 0) {
            return null;
        }

        const {hasInEstate, hasOutOfEstate, showOnlyFullEstate} = getAvailableEstateTypes(estateFlowchartStateData);

        return (
            <>
                {(hasInEstate && !showOnlyFullEstate) &&
                    <PrintViewWrapper isCallFromDEFReport={true} displayName={displayName}
                                      key={"in-estate-flowchart"}
                                      enableEstateFlowchartMVPRelease={releaseToggles?.enableEstateFlowchartMVPRelease}>
                        <EstateFlowchart profileId={id} selectedFlowchartId={selectedFlowchartId}
                                         estateName={"In Estate"}/>
                    </PrintViewWrapper>
                }
                {
                    releaseToggles?.enableEstateFlowchartMVPRelease &&
                    Object.entries(trustToCardIdsMap)
                        .filter(([trustId]) => (trustId === estateFlowchartStateData?.revocableTrust?.trustId
                            || trustId === estateFlowchartStateData?.willSummary?.primaryWill?.trustId
                            || trustId === estateFlowchartStateData?.willSummary?.partnerWill?.trustId))
                        .map(([trustId, cardIds]) =>
                            renderCardDetails(
                                estateFlowchartStateData,
                                cardIds,
                                FlowchartEstateItemType.REVOCABLE,
                                estateFlowchartStateData?.revocableTrust?.nickName ?? "",
                                trustId,
                                selectedFlowchartId,
                                displayName
                            )
                        )
                }
                {releaseToggles?.enableWildCardInEstateFlowchart && renderMiscTrusts(estateFlowchartStateData, EstateToggle.IN_ESTATE)}
                {(hasOutOfEstate && !showOnlyFullEstate) &&
                    <PrintViewWrapper isCallFromDEFReport={true} displayName={displayName}
                                      key={"out-of-estate-flowchart"}
                                      enableEstateFlowchartMVPRelease={releaseToggles?.enableEstateFlowchartMVPRelease}>
                        <EstateFlowchart profileId={id} selectedFlowchartId={selectedFlowchartId}
                                         estateName={"Out of Estate"}/>
                    </PrintViewWrapper>
                }
                {
                    releaseToggles?.enableEstateFlowchartMVPRelease &&
                    renderIrrevocableTrusts(estateFlowchartStateData, trustToCardIdsMap, selectedFlowchartId, displayName)
                }
                {releaseToggles?.enableWildCardInEstateFlowchart && renderMiscTrusts(estateFlowchartStateData, EstateToggle.OUT_OF_ESTATE)}
            </>
        );
    }, [fullEstateRendered, estateFlowchartStateData, trustToCardIdsMap]);

    if (loading) {
        return <LoadingIndicator/>;
    }

    return (
        <>
            {
                estateFlowchartStateDataList?.map((estateFlowchartStateDataItem, index) => (
                    <PrintViewWrapper isCallFromDEFReport={true} displayName={displayName}
                                      key={`documents-reviewed-page-${index}`}
                                      enableEstateFlowchartMVPRelease={releaseToggles?.enableEstateFlowchartMVPRelease}>
                        <DocumentsReviewed estateFlowchartStateData={estateFlowchartStateDataItem} index={index}/>
                    </PrintViewWrapper>
                ))
            }
            <div ref={fullEstateRendered}>
                <PrintViewWrapper isCallFromDEFReport={true} displayName={displayName}
                                  key={"full-estate-flowchart"}
                                  enableEstateFlowchartMVPRelease={releaseToggles?.enableEstateFlowchartMVPRelease}>
                    <EstateFlowchart profileId={id} selectedFlowchartId={selectedFlowchartId}
                                     estateName={"Full Estate"}/>
                </PrintViewWrapper>
            </div>

            {renderEstateItemsAndCardDetailsSplitView}
        </>
    );
}

export default EstateFlowchartReportWrapper;

